// Shared presentational blocks. Pure functions: data in → HTML out.
import { html, raw, esc } from '../lib/dom';
import { icon } from './icons';
import { assetThumb, coverThumb } from './thumb';
import { initials, relTime, slugStatus, titleCase, money, dur } from '../lib/format';
import * as store from '../store/ui';

export const STATUS_LABEL: any = { in_production: 'In production', partial_outage: 'Partial outage' };

export function status(s?: any, label?: any) {
  return html`<span class="status ${slugStatus(s)}"><span class="dot"></span>${label || STATUS_LABEL[s] || titleCase(s)}</span>`;
}

export function avatar(user?: any, cls: any = '') {
  if (!user) return '';
  const bg = user.is_agent ? 'var(--indigo)' : `hsl(${user.avatar_hue ?? 150} 38% 38%)`;
  return html`<span class="avatar ${cls}" style="background:${raw(bg)}" title="${user.name}">${user.is_agent ? raw('AI') : initials(user.name)}</span>`;
}

export function favButton(type?: any, id?: any) {
  const on = store.isFavorite(type, id);
  return html`<button class="icon-btn fav ${on ? 'on' : ''}" data-fav="${type}:${id}" title="${on ? 'Remove from favorites' : 'Add to favorites'}" aria-pressed="${on}">${icon(on ? 'starFill' : 'star')}</button>`;
}

export function tabs(items?: any, active?: any, { attr = 'data-tab' }: any = {}) {
  return html`<div class="tabs" role="tablist">${items.map(([key, label, n]: any) =>
    html`<button class="tab ${key === active ? 'on' : ''}" role="tab" aria-selected="${String(key === active)}" ${raw(attr)}="${key}">${label}${n != null ? html`<span class="n">${n}</span>` : ''}</button>`)}</div>`;
}

export function select(name?: any, options?: any, value?: any, { placeholder, cls = '' }: any = {}) {
  return html`<select class="select sm ${cls}" data-filter="${name}" aria-label="${placeholder || name}">
    ${placeholder ? html`<option value="">${placeholder}</option>` : ''}
    ${options.map(([v, l]: any) => html`<option value="${v}" ${raw(String(v) === String(value ?? '') ? 'selected' : '')}>${l}</option>`)}
  </select>`;
}

export function searchInput(value?: any, placeholder: any = 'Search…', name: any = 'q') {
  return html`<label class="search">${icon('search')}<input class="input sm" data-filter="${name}" value="${value || ''}" placeholder="${placeholder}" autocomplete="off"></label>`;
}

export function empty(text?: any, action: any = '') { return html`<div class="empty">${text}${action ? html`<div style="margin-top:12px">${action}</div>` : ''}</div>`; }

export function sectionHead(title?: any, link?: any, linkLabel: any = 'View all') {
  return html`<div class="section-head"><h2>${title}</h2>${link ? html`<a class="link" href="${link}">${linkLabel} →</a>` : ''}</div>`;
}

export function typeTag(type?: any) {
  const color = ({ IMAGE: 'var(--t-image)', REFERENCE: 'var(--t-image)', VIDEO: 'var(--t-video)', AUDIO: 'var(--t-audio)', TEXT: 'var(--t-text)', PROMPT: 'var(--t-text)', CHARACTER: 'var(--role-model)', MODEL: 'var(--role-model)', MODEL_3D: 'var(--t-3d)', LORA: 'var(--role-model)', TOOL: 'var(--role-tool)', EFFECT: 'var(--t-3d)', FLOW_TEMPLATE: 'var(--role-input)', TEMPLATE: 'var(--role-input)', PRESET: 'var(--t-json)', STYLE: 'var(--role-output)', DATASET: 'var(--t-json)' } as Record<string, string>)[type] || 'var(--muted)';
  return html`<span class="type-tag" style="color:${raw(color)}">${type.replace('_', ' ')}</span>`;
}

// Cards
export function projectCard(p?: any, { workspace, brands = [], stats }: any = {}) {
  return html`<a class="card hover entity-card" href="#/projects/${p.id}">
    ${coverThumb(p)}
    <div class="body">
      <div class="row"><span class="title ellipsis">${p.name}</span><span class="spacer"></span>${status(p.status)}</div>
      <div class="meta">${brands.map((b?: any) => html`<span class="chip">${b.name}</span>`)}<span>${workspace?.name || ''}</span></div>
      ${stats ? html`<div class="meta"><span>${stats.flows} flows</span><span>·</span><span>${stats.runs} executions</span><span>·</span><span>updated ${relTime(p.updated_at)}</span></div>` : ''}
    </div>
  </a>`;
}

export function assetCard(a?: any, owner?: any) {
  return html`<a class="card hover entity-card" href="${a.data?.origin === 'remotion-template' || a.origin === 'remotion-template' ? `#/assets/templates/${a.id}/edit` : `#/assets/${a.id}`}">
    ${assetThumb(a)}
    <div class="body">
      <div class="title ellipsis" title="${a.name}">${a.name}</div>
      <div class="meta">${typeTag(a.type)}<span>·</span><span class="ellipsis">${owner}</span></div>
      <div class="meta">${status(a.status.toLowerCase(), a.status)}${a.current_version > 1 ? html`<span class="chip">v${a.current_version}</span>` : ''}</div>
    </div>
  </a>`;
}

export function assetRow(a?: any, owner?: any, extra: any = '') {
  return html`<a class="list-row" href="#/assets/${a.id}">${assetThumb(a)}<div class="col" style="gap:2px;flex:1"><span class="t ellipsis">${a.name}</span><span class="s">${a.type} · ${owner}</span></div>${extra}</a>`;
}

export function runRow(r: any, { flow, project }: any) {
  return html`<a class="list-row run-list-row" href="${r.historical ? (flow && project ? `#/projects/${project.id}/flows/${flow.id}` : '#/executions') : `#/executions/${r.id}`}">
    <span class="run-list-heading"><span class="t ellipsis">${flow?.name || 'Untitled flow'}</span>${status(r.status)}</span>
    <span class="run-list-details"><span class="s ellipsis">${project?.name || 'Unknown project'}</span><span class="s run-list-id">#${r.number}</span>
      <span class="s run-list-duration">${r.finished_at && r.started_at ? dur(Date.parse(r.finished_at) - Date.parse(r.started_at)) : r.status === 'running' ? 'running…' : ''}</span></span>
  </a>`;
}

export function kpi(label?: any, value?: any, sub: any = '') {
  return html`<div class="card kpi"><span class="eyebrow">${label}</span><span class="v">${value}</span>${sub ? html`<span class="d">${sub}</span>` : ''}</div>`;
}

export function pageHead({ eyebrow, title, lede, actions = '' }: any) {
  return html`<header class="page-head"><div>${eyebrow ? html`<div class="eyebrow">${eyebrow}</div>` : ''}<h1>${title}</h1>${lede ? html`<p class="lede">${lede}</p>` : ''}</div><div class="row wrap">${actions}</div></header>`;
}

export function kv(pairs?: any) {
  return html`<dl class="kv">${pairs.filter(Boolean).map(([k, v]: any) => html`<dt>${k}</dt><dd>${v}</dd>`)}</dl>`;
}

export function json(obj?: any) { return html`<pre class="code">${JSON.stringify(obj, null, 2)}</pre>`; }

export function table(columns?: any, rows?: any, { rowHref, empty: emptyText = 'Nothing here yet' }: any = {}) {
  if (!rows.length) return empty(emptyText);
  return html`<div class="table-wrap"><table class="table">
    <thead><tr>${columns.map((c?: any) => html`<th class="${c.num ? 'num' : ''}">${c.label}</th>`)}</tr></thead>
    <tbody>${rows.map((r?: any) => {
      const href = rowHref?.(r);
      return html`<tr class="${href ? 'click' : ''}" ${href ? raw(`data-href="${esc(href)}"`) : ''}>${columns.map((c?: any) => html`<td class="${c.num ? 'num' : c.mono ? 'mono' : ''}">${c.render(r)}</td>`)}</tr>`;
    })}</tbody></table></div>`;
}

export const moneyCell = (n?: any) => money(n, n < 1 ? 3 : 2);
